import {
    getAllAttributeValueOptions,
    isValueAvailableForAttribute,
} from '../ProductStoreOverview/variantUtils';
import './ProductVariantSelector.scss';

const optionClassName = (option) => {
    const names = ['product-variant-option'];
    if (option.selected) names.push('product-variant-option--selected');
    else if (option.compatible) names.push('product-variant-option--available');
    else names.push('product-variant-option--disabled');
    return names.join(' ');
};

const buildAttributeGroups = (product, attrs, attrMap, selectedVariant) => {
    if (!product || !attrs?.length) return [];
    return attrs
        .map((attr) => {
            const options = getAllAttributeValueOptions(product, attr.key, attrMap);
            const currentVal = selectedVariant?.[attr.key] ?? '';
            return {
                key: attr.key,
                name: attr.name,
                options: options.map((opt) => ({
                    id: opt.valueId,
                    label: opt.label,
                    selected: String(opt.valueId) === String(currentVal),
                    compatible: isValueAvailableForAttribute(
                        product,
                        attr.key,
                        opt.valueId,
                        attrMap,
                        selectedVariant,
                    ),
                })),
            };
        })
        .filter((group) => group.options.length > 0);
};

/**
 * Shared store variant UI for list cards and product detail.
 * `size` is the only visual difference (`compact` vs `detail`).
 */
export default function ProductVariantSelector({
    size = 'detail',
    title,
    product,
    attrs,
    attrMap,
    selectedVariant,
    groups: groupsProp,
    onSelect,
    selectLabel,
    selectValue,
    selectOptions,
    onSelectChange,
}) {
    const groups = groupsProp
        || buildAttributeGroups(product, attrs, attrMap, selectedVariant);
    const hasSelect = Array.isArray(selectOptions) && selectOptions.length > 0;
    if (!groups.length && !hasSelect) return null;

    const rootClass = [
        'product-variant-selector',
        size === 'compact'
            ? 'product-variant-selector--compact'
            : 'product-variant-selector--detail',
    ].join(' ');

    return (
        <div className={rootClass}>
            {title ? <h6 className="product-variant-selector__title">{title}</h6> : null}

            {hasSelect ? (
                <div className="product-variant-group">
                    {selectLabel ? (
                        <div className="product-variant-group__name">{selectLabel}</div>
                    ) : null}
                    <select
                        className="product-variant-select"
                        value={selectValue || ''}
                        onChange={(e) => onSelectChange?.(e.target.value || null)}
                    >
                        {selectOptions.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
            ) : null}

            {groups.map((group) => (
                <div key={group.key} className="product-variant-group">
                    {group.name ? (
                        <div className="product-variant-group__name">{group.name}</div>
                    ) : null}
                    <div className="product-variant-group__options" role="group" aria-label={group.name}>
                        {group.options.map((option) => (
                            <button
                                key={option.id}
                                type="button"
                                className={optionClassName(option)}
                                aria-pressed={option.selected}
                                onClick={() => onSelect?.(group.key, option.id)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
